// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/** Longest art-resolution side we ever dither, to bound error-diffusion cost. */
export const MAX_ART_LONG = 1400;
/** Longest output side after nearest-neighbour upscale. */
export const MAX_OUTPUT_LONG = 4000;

export interface SizePlan {
  /** Working (art) resolution the image is dithered at. */
  artWidth: number;
  artHeight: number;
  /** Nearest-neighbour upscale factor applied to produce the crisp output. */
  scale: number;
  /** Final output resolution. */
  outWidth: number;
  outHeight: number;
}

/**
 * Decide the art resolution and upscale factor for a source image at the chosen
 * `pixelSize` (each art pixel represents pixelSize×pixelSize source pixels).
 *
 * The art side is `src / pixelSize`, capped at MAX_ART_LONG. The output is the
 * art image scaled back up by `pixelSize` (so it reads as chunky pixels at
 * roughly the original size), capped at MAX_OUTPUT_LONG.
 */
export function planSize(srcW: number, srcH: number, pixelSize: number): SizePlan {
  const w = Math.max(1, Math.round(srcW));
  const h = Math.max(1, Math.round(srcH));
  const px = Math.max(1, Math.round(pixelSize));

  let artW = Math.max(1, Math.round(w / px));
  let artH = Math.max(1, Math.round(h / px));
  const artLong = Math.max(artW, artH);
  if (artLong > MAX_ART_LONG) {
    const k = MAX_ART_LONG / artLong;
    artW = Math.max(1, Math.round(artW * k));
    artH = Math.max(1, Math.round(artH * k));
  }

  let scale = px;
  const outLong = Math.max(artW, artH) * scale;
  if (outLong > MAX_OUTPUT_LONG) {
    scale = Math.max(1, Math.floor(MAX_OUTPUT_LONG / Math.max(artW, artH)));
  }

  return {
    artWidth: artW,
    artHeight: artH,
    scale,
    outWidth: artW * scale,
    outHeight: artH * scale,
  };
}

/** Human-readable byte size. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/** Formats we accept for import. */
export const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/avif',
];

export function isAcceptedImage(file: { type: string; name?: string }): boolean {
  if (file.type && ACCEPTED_TYPES.includes(file.type)) return true;
  const name = (file.name ?? '').toLowerCase();
  return /\.(png|jpe?g|webp|gif|bmp|avif)$/.test(name);
}
